import React, { useEffect, useState } from "react";
import { withTranslation } from "react-i18next";
import PropTypes from "prop-types";
import { getDateFormat } from "@utils/datePickerUtils";
import { useGetGeneralParameters } from "@hooks/useGeneralParams";
import EnquiryPriority from "../../../EnquiryList/components/EnquiryPriority";
import {
  GenrealInfo,
  FlexRow,
  FlexRowHeader,
  GridRow,
  SubTitle,
  SubTitleHeader,
  DescriptionData,
  FlexRowAddress,
  MapContainer,
} from "./GeneralInfo.styled";
import UserInfo from "@utils/UserInfo";
import initMap from "@utils/map";
import { addressSingleLine } from "@utils/utils";

const GeneralInfo = (props) => {
  const { t, data } = props;
  const [headerColour, setHeaderColour] = useState("#E83F1E");
  const [dateText, setDateText] = useState("ENQUIRY_PRIORITY_LOW");

  const countryId = UserInfo.countryId();
  const generalParams = useGetGeneralParameters();
  const { countryList } = generalParams;

  useEffect(() => {
    if (
      !!data.destination.position.latitude &&
      !!data.destination.position.longitude
    )
      initMap(
        "mapGeneral",
        data.destination.position.latitude,
        data.destination.position.longitude,
        data.destination.position.latitude,
        data.destination.position.longitude,
        false
      );
  }, []);

  useEffect(() => {
    if (parseInt(data?.priority, 10) === 3) {
      setHeaderColour("#52AD00");
      setDateText("ENQUIRY_PRIORITY_LOW");
    }
    if (parseInt(data?.priority, 10) === 2) {
      setHeaderColour("#EAA000");
      setDateText("ENQUIRY_PRIORITY_NORMAL");
    }
    if (parseInt(data?.priority, 10) === 1) {
      setHeaderColour("#D04816");
      setDateText("ENQUIRY_PRIORITY_HIGH");
    }
  }, []);

  return (
    <>
      <GenrealInfo>
        {data.step === 1 && (
          <FlexRowHeader>
            <EnquiryPriority data={data} page="requestView" />
            <SubTitleHeader
              marginBottom="0px"
              color={headerColour}
              paddingLeft="10px"
            >
              {t(dateText) + " - " + t("ENQUIRY_DUE_DATE") + " "}
              {getDateFormat(data.dueDate, "YYYY-MM-DD")}
            </SubTitleHeader>
          </FlexRowHeader>
        )}
        {data.step !== 1 && <FlexRowHeader></FlexRowHeader>}
        <GridRow column="1fr 1fr 1fr">
          <div>
            <FlexRow>
              <SubTitle fontWeight="700">{t("ENQUIRY_DATES")} :</SubTitle>
              <SubTitle color="#767676" paddingLeft="10px">
                {data.dateType === 1 &&
                  getDateFormat(data.startDate, "YYYY-MM-DD")}
                {data.dateType !== 1 &&
                  t("GENERAL_FROM") +
                    " " +
                    getDateFormat(data.startDate, "YYYY-MM-DD") +
                    " " +
                    t("GENERAL_TO") +
                    " " +
                    getDateFormat(data.endDate, "YYYY-MM-DD")}
              </SubTitle>
            </FlexRow>
            <FlexRow>
              <SubTitle fontWeight="700">{t("ENQUIRY_QUANTITY")} :</SubTitle>
              <SubTitle color="#767676" paddingLeft="10px">
                {data?.quantity + " " + t(data?.unit)}
              </SubTitle>
            </FlexRow>
            <FlexRow>
              <SubTitle fontWeight="700">
                {t("ENQUIRY_ELIGIBLE_BACKFILLING")} :
              </SubTitle>
              <SubTitle color="#767676" paddingLeft="10px">
                {data?.isEligibleBackfilling ? t("YES") : t("NO")}
              </SubTitle>
            </FlexRow>

            {data?.site?.platformCountry?.id === 1 && (
              <FlexRow>
                <SubTitle fontWeight="700">
                  {t("ENQUIRY_PURCHASE_ORDER")} :
                </SubTitle>
                <SubTitle color="#767676" paddingLeft="10px">
                  {data?.purchaseOrder}
                </SubTitle>
              </FlexRow>
            )}
            {!!data?.analysis && (
              <FlexRow>
                <SubTitle fontWeight="700">
                  {t("ENQUIRY_COMPLEMENTARY_ANALYSIS")} :
                </SubTitle>
                <SubTitle color="#767676" paddingLeft="10px">
                  {data?.analysis}
                </SubTitle>
              </FlexRow>
            )}
            <FlexRow>
              <SubTitle fontWeight="700">
                {t("ENQUIRY_LOADS_PER_DAY")} :
              </SubTitle>
              <SubTitle color="#767676" paddingLeft="10px">
                {data?.loadsPerDay + " " + t("LOADS")}
              </SubTitle>
            </FlexRow>
            {data?.requestedPrice !== null && (
              <FlexRow>
                <SubTitle fontWeight="700">
                  {t("ENQUIRY_REQUESTED_PRICE")} :
                </SubTitle>
                <SubTitle color="#767676" paddingLeft="10px">
                  {data?.requestedPrice}
                </SubTitle>
              </FlexRow>
            )}
            <FlexRow>
              <SubTitle fontWeight="700">{t("ENQUIRY_TRUCK_TYPES")} :</SubTitle>
              <SubTitle color="#767676" paddingLeft="10px">
                {data.trucks !== null &&
                  data.trucks.map((truck) => truck.name).join(", ")}
              </SubTitle>
            </FlexRow>
          </div>
          <div>
            {!!data.destination.position.latitude &&
              !!data.destination.position.longitude && (
                <MapContainer id="mapGeneral" />
              )}
            <FlexRowAddress>
              <FlexRow>
                <SubTitle fontWeight="700">{t("SITE_ADDRESS")} :</SubTitle>
                <SubTitle color="#767676" paddingLeft="10px">
                  {addressSingleLine(
                    countryId,
                    data?.site?.address,
                    countryList
                  )}
                </SubTitle>
              </FlexRow>
              {data?.product?.category?.id === 4 && (
                <FlexRow>
                  <SubTitle fontWeight="700">{t("DESTINATION")} :</SubTitle>
                  <SubTitle color="#767676" paddingLeft="10px">
                    {addressSingleLine(
                      countryId,
                      data?.destination,
                      countryList
                    )}
                  </SubTitle>
                </FlexRow>
              )}
            </FlexRowAddress>
          </div>
        </GridRow>
        {data?.comment !== null && (
          <>
            <GridRow column="1fr">
              <SubTitle fontWeight="700" marginBottom="5px">
                {t("ENQUIRY_CLIENT_INFORMATION")} :
              </SubTitle>
            </GridRow>
            <DescriptionData>
              <div
                dangerouslySetInnerHTML={{
                  __html: data.comment,
                }}
              ></div>
            </DescriptionData>
          </>
        )}
        <GridRow column="1fr">
          <SubTitle fontWeight="700" marginBottom="5px">
            {t("ENQUIRY_HESUS_INFORMATION")} :
          </SubTitle>
        </GridRow>
        <DescriptionData>
          {data?.internalComment ? (
            <div
              dangerouslySetInnerHTML={{
                __html: data.internalComment,
              }}
            ></div>
          ) : (
            "-"
          )}
        </DescriptionData>
        <GridRow column="1fr">
          <SubTitle fontWeight="700" marginBottom="5px">
            {t("ENQUIRY_POLLUTION_INFORMATION")} :
          </SubTitle>
        </GridRow>
        <DescriptionData>
          {data?.adminComment ? (
            <div
              dangerouslySetInnerHTML={{
                __html: data.adminComment,
              }}
            ></div>
          ) : (
            "-"
          )}
        </DescriptionData>
      </GenrealInfo>
    </>
  );
};
GeneralInfo.propTypes = {
  t: PropTypes.func.isRequired,
  // data: { adminComment: PropTypes.string, internalComment: PropTypes.string },
};
GeneralInfo.defaultProps = {
  // data: { adminComment: null, internalComment: null },
};

export default withTranslation()(GeneralInfo);
